/* eslint eslint-comments/no-use: off */
import {Timeline} from '@primer/react'
import type {CommentBoxConfig} from '@github-ui/comment-box'
import {
  AddedToProjectEvent,
  MovedColumnsInProjectEvent,
  RemovedFromProjectEvent,
  SubscribedEvent,
  UnsubscribedEvent,
  MentionedEvent,
  ClosedEvent,
  ReopenedEvent,
  RenamedTitleEvent,
  LockedEvent,
  UnlockedEvent,
  PinnedEvent,
  UnpinnedEvent,
  LabeledEvent,
  UnlabeledEvent,
  UnassignedEvent,
  AssignedEvent,
  CommentDeletedEvent,
  UserBlockedEvent,
  MilestonedEvent,
  DemilestonedEvent,
  CrossReferencedEvent,
  ReferencedEvent,
  ConnectedEvent,
  TransferredEvent,
  ConvertedNoteToIssueEvent,
  DisconnectedEvent,
  MarkedAsDuplicateEvent,
  UnmarkedAsDuplicateEvent,
  ConvertedToDiscussionEvent,
  AddedToProjectV2Event,
  RemovedFromProjectV2Event,
  ProjectV2ItemStatusChangedEvent,
  ConvertedFromDraftEvent,
  VALUES,
  TimelineRowBorder,
  FallbackEvent,
} from '@github-ui/timeline-items'
import type {
  HighlightedEvent,
  LockedEventViewer$key,
  ReferenceTypes,
  TimelineRowBorderCommentParams,
} from '@github-ui/timeline-items'
import {IssueComment} from '@github-ui/commenting'
import {createElement, useCallback} from 'react'
import {graphql, useFragment} from 'react-relay'

import {TEST_IDS} from '../constants'
import type {TimelineItemsPaginated$key} from './__generated__/TimelineItemsPaginated.graphql'
import {rollupEvents, type RolledUpTimelineItem, type TimelineItem} from '../utils/timeline-rollups'
import type {TimelineItemsViewer$key} from './__generated__/TimelineItemsViewer.graphql'

export type TimelineBaseProps = {
  issueRef?: LockedEventViewer$key
  viewer: TimelineItemsViewer$key
  commentBoxConfig?: CommentBoxConfig
  onCommentChange?: (id: string) => void
  onCommentEditCancel?: (id: string) => void
  onCommentReply: (quotedComment: string) => void
  onLinkClick?: (event: MouseEvent) => void
  navigate: (url: string) => void
  timelineEventBaseUrl?: string
  withLiveUpdates?: boolean
  relayConnectionIds: string[]
  commentSubjectAuthorLogin?: string
  highlightedEvent?: HighlightedEvent
}

export type TimelineItemsProps = {
  issue: TimelineItemsPaginated$key
  highlightedCommentId?: string
  timelineEventBaseUrl?: string
  issueUrl: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  type: string
} & TimelineBaseProps

type timelineProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryRef: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rollupGroup?: Record<string, any[]>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  issueRef?: any
  issueUrl: string
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  referenceTypes?: ReferenceTypes
  viewerLogin: string
}

export const TIMELINE_ITEMS: Record<string, ((args: timelineProps) => JSX.Element) | undefined> = {
  AddedToProjectEvent,
  MovedColumnsInProjectEvent,
  RemovedFromProjectEvent,
  SubscribedEvent,
  UnsubscribedEvent,
  MentionedEvent,
  ClosedEvent,
  ReopenedEvent,
  RenamedTitleEvent,
  LockedEvent,
  UnlockedEvent,
  PinnedEvent,
  UnpinnedEvent,
  LabeledEvent,
  UnlabeledEvent,
  UnassignedEvent,
  AssignedEvent,
  CommentDeletedEvent,
  UserBlockedEvent,
  MilestonedEvent,
  DemilestonedEvent,
  CrossReferencedEvent,
  ReferencedEvent,
  ConnectedEvent,
  TransferredEvent,
  ConvertedNoteToIssueEvent,
  DisconnectedEvent,
  MarkedAsDuplicateEvent,
  UnmarkedAsDuplicateEvent,
  ConvertedToDiscussionEvent,
  AddedToProjectV2Event,
  RemovedFromProjectV2Event,
  ProjectV2ItemStatusChangedEvent,
  ConvertedFromDraftEvent,
}

export function TimelineItems({
  issue,
  issueRef,
  onCommentChange,
  onCommentEditCancel,
  onCommentReply,
  highlightedEvent,
  timelineEventBaseUrl,
  issueUrl,
  type,
  viewer,
  ...commentArgs
}: TimelineItemsProps) {
  const data = useFragment(
    graphql`
      fragment TimelineItemsPaginated on IssueTimelineItemsConnection {
        edges {
          node {
            ... on IssueComment {
              databaseId # this is required for highlighting support as this is a major event
              viewerDidAuthor # this is required for giving the viewer's comments a different border color
              issue {
                author {
                  login
                }
              }
            }
            ... on ReferencedEvent {
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              createdAt
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              actor {
                login
              }
            }
            ... on CrossReferencedEvent {
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              createdAt
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              actor {
                login
              }
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              source {
                __typename
              }
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              willCloseTarget
            }
            ... on MentionedEvent {
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              actor {
                login
              }
            }
            ... on LabeledEvent {
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              createdAt
            }
            ... on UnlabeledEvent {
              # eslint-disable-next-line relay/unused-fields Required for the rollup calculations
              createdAt
            }
            __id
            __typename
            ...IssueComment_issueComment
            ...ReactionViewerGroups
            ...AddedToProjectEvent
            ...MovedColumnsInProjectEvent
            ...RemovedFromProjectEvent
            ...SubscribedEvent
            ...UnsubscribedEvent
            ...MentionedEvent
            ...ClosedEvent
            ...ReopenedEvent
            ...LockedEvent
            ...UnlockedEvent
            ...PinnedEvent
            ...UnpinnedEvent
            ...LabeledEvent
            ...RenamedTitleEvent
            ...UnlabeledEvent
            ...UnassignedEvent
            ...AssignedEvent
            ...CommentDeletedEvent
            ...UserBlockedEvent
            ...MilestonedEvent
            ...DemilestonedEvent
            ...CrossReferencedEvent
            ...ReferencedEvent
            ...ConnectedEvent
            ...TransferredEvent
            ...ConvertedNoteToIssueEvent
            ...DisconnectedEvent
            ...MarkedAsDuplicateEvent
            ...UnmarkedAsDuplicateEvent
            ...ConvertedToDiscussionEvent
            ...AddedToProjectV2Event
            ...RemovedFromProjectV2Event
            ...ProjectV2ItemStatusChangedEvent
            ...ConvertedFromDraftEvent
          }
        }
      }
    `,
    issue,
  )
  const timelineViewer = useFragment(
    graphql`
      fragment TimelineItemsViewer on User {
        login
        ...ReactionViewerUser
      }
    `,
    viewer,
  )

  const validTimelineItems = (data?.edges || []).flatMap(a => (a ? [a.node] : []))
  const rolledUpTimelineItems = rollupEvents(validTimelineItems)

  // Extract just the items from the rollup groups
  const timelineItemsWithoutRollupGroups = rolledUpTimelineItems.map(({item}) => item) as TimelineItem[]

  const getTimelineItemRenderer = useCallback(
    (rolledUpItem: RolledUpTimelineItem, index: number) => {
      const {item, rollupGroup} = rolledUpItem

      // Handling items we couldn't fetch
      if (!item) {
        return (
          <TimelineRowBorder
            key={`failed-timeline-item-${index}`}
            addDivider={true}
            item={{__id: `failed-timeline-item-${index}`}}
            isMajor={false}
            isHighlighted={false}
          >
            <FallbackEvent />
          </TimelineRowBorder>
        )
      }

      const highlightedEventId =
        item.__typename !== 'IssueComment' && highlightedEvent?.prefix !== 'issuecomment'
          ? highlightedEvent?.id
          : undefined
      const highlightedCommentId =
        item.__typename === 'IssueComment' && highlightedEvent?.prefix === 'issuecomment'
          ? highlightedEvent?.id
          : undefined

      const isMajorEvent = VALUES.timeline.majorEventTypes.includes(item.__typename)

      const addDivider = shouldAddDivider(timelineItemsWithoutRollupGroups, index)

      const timelineReactElement = TIMELINE_ITEMS[item.__typename]

      let itemToRender = null
      let commentParams: TimelineRowBorderCommentParams | undefined = undefined

      if (item.__typename === 'IssueComment') {
        commentParams = {
          first: index === 0 || timelineItemsWithoutRollupGroups[index - 1]?.__typename !== 'IssueComment',
          last:
            index === timelineItemsWithoutRollupGroups.length - 1 ||
            timelineItemsWithoutRollupGroups[index + 1]?.__typename !== 'IssueComment',
          viewerDidAuthor: item?.viewerDidAuthor,
        }
        itemToRender = (
          <IssueComment
            comment={item}
            commentSubjectAuthorLogin={item.issue?.author?.login ?? ''}
            reactionGroups={item}
            onChange={() => onCommentChange && onCommentChange(item.__id)}
            onEditCancel={() => onCommentEditCancel && onCommentEditCancel(item.__id)}
            onReply={quotedComment => onCommentReply(quotedComment)}
            onSave={() => onCommentEditCancel && onCommentEditCancel(item.__id)}
            highlightedCommentId={highlightedCommentId}
            viewer={timelineViewer}
            {...commentArgs}
          />
        )
      }

      if (timelineReactElement) {
        itemToRender = createElement(timelineReactElement, {
          queryRef: item,
          rollupGroup,
          key: item.__id,
          issueRef,
          issueUrl,
          highlightedEventId,
          timelineEventBaseUrl: timelineEventBaseUrl || '/issues',
          refAttribute: commentArgs.refAttribute,
          viewerLogin: timelineViewer.login,
        })
      }

      const isHighlighted = (highlightedCommentId || highlightedEventId) === String(item.databaseId)

      return (
        <TimelineRowBorder
          key={item.__id}
          addDivider={addDivider}
          item={item}
          isMajor={isMajorEvent && VALUES.timeline.borderedMajorEventTypes.includes(item.__typename)}
          isHighlighted={isHighlighted}
          commentParams={commentParams}
        >
          {itemToRender}
        </TimelineRowBorder>
      )
    },
    [
      commentArgs,
      highlightedEvent?.id,
      highlightedEvent?.prefix,
      issueRef,
      issueUrl,
      onCommentChange,
      onCommentEditCancel,
      onCommentReply,
      timelineEventBaseUrl,
      timelineItemsWithoutRollupGroups,
      timelineViewer,
    ],
  )

  return (
    <Timeline data-testid={TEST_IDS.issueTimeline(type)}>
      {rolledUpTimelineItems
        .map((rolledUpItem, index) => {
          const renderItem = getTimelineItemRenderer(rolledUpItem, index)
          return {type: rolledUpItem.item?.__typename, item: renderItem, processed: false}
        })
        // This is grouping events that are rendered next to eachother
        // inside <section> tags for adding the correct landmarks for screenreaders
        .reduce((newArr, currentItem, currentIndex, allItems) => {
          if (currentItem.processed) return newArr
          if (currentItem.type === 'IssueComment') {
            currentItem.processed = true
            newArr.push(currentItem.item)
            return newArr
          }

          const endSectionIndex = rolledUpTimelineItems.findIndex((item, index) => {
            if (index > currentIndex && item.item?.__typename === 'IssueComment') return index
            return false
          })
          const sectionElement = (
            <section key={`events-${currentIndex}`} aria-label="Events">
              {allItems.slice(currentIndex, endSectionIndex > -1 ? endSectionIndex : undefined).map(item => {
                item.processed = true
                return item.item
              })}
            </section>
          )

          newArr.push(sectionElement)
          return newArr
        }, [] as JSX.Element[])}
    </Timeline>
  )
}

function shouldAddDivider(items: TimelineItem[], index: number) {
  if (index > 0 && items[index - 1]) {
    const eventType = items[index]!.__typename
    const previousEventType = items[index - 1]!.__typename
    const isMajorEvent = VALUES.timeline.majorEventTypes.includes(eventType)
    const previousIsMajorEvent = VALUES.timeline.majorEventTypes.includes(previousEventType)

    if (isMajorEvent === previousIsMajorEvent) {
      if (eventType === 'IssueComment' && previousEventType === 'IssueComment') {
        return false
      } else if (eventType !== 'IssueComment' && previousEventType !== 'IssueComment') {
        return false
      }
    }
  }
  return true
}

try{ TIMELINE_ITEMS.displayName ||= 'TIMELINE_ITEMS' } catch {}
try{ TimelineItems.displayName ||= 'TimelineItems' } catch {}